import React, { useState } from 'react';
import { useSubscriptionUsage } from '../hooks/useSubscriptionUsage';
import UsageIndicator from './UsageIndicator';
import { Link } from 'react-router-dom';
import { SubscriptionPlan } from '../services/subscriptionService';
import { AlertTriangle, CheckCircle, RefreshCw, BarChart, Calendar, ArrowUpRight, ChevronRight } from 'lucide-react';

interface UsageDashboardProps {
  className?: string;
  showDetailedView?: boolean;
}

/**
 * UsageDashboard Component
 * 
 * A comprehensive dashboard displaying the user's subscription status,
 * usage statistics, and plan details. Includes upgrade prompts and
 * account information.
 */
const UsageDashboard: React.FC<UsageDashboardProps> = ({ 
  className = '',
  showDetailedView = true 
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');
  const usageData = useSubscriptionUsage();
  
  const {
    isLoading,
    error,
    subscription,
    usedCount,
    limit,
    remaining,
    percentage,
    fetchUsage,
    hasActiveSubscription,
    planType,
    isPremium,
    isStandard,
    isFree,
    usageLevel
  } = usageData;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUsage();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Calculate renewal date for display (first of next month)
  const today = new Date();
  const renewalDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const formattedRenewalDate = renewalDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Days until renewal
  const daysUntilRenewal = Math.ceil((renewalDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  // Generate plan displays
  const planIcon = (plan: SubscriptionPlan) => {
    switch(plan) {
      case SubscriptionPlan.PREMIUM:
        return '✨';
      case SubscriptionPlan.STANDARD:
        return '⭐';
      default:
        return '•';
    }
  };

  const getPlanColor = () => {
    if (isPremium) return 'text-purple-600';
    if (isStandard) return 'text-blue-600';
    return 'text-gray-600';
  };

  // Generate synthetic usage history data
  const generateUsageHistory = () => {
    const days = Math.min(30, today.getDate());
    const data = [];
    
    // Create synthetic daily usage based on current usage pattern
    const dailyAverage = usedCount / days;
    
    for (let i = 0; i < days; i++) {
      // Add some randomness to make it look realistic
      const randomFactor = 0.5 + Math.random();
      const value = Math.round(dailyAverage * randomFactor);
      
      const date = new Date(today);
      date.setDate(today.getDate() - days + i);
      
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: value
      });
    }
    
    return data;
  };
  
  const usageHistory = generateUsageHistory();
  const highestDailyUsage = Math.max(...usageHistory.map(day => day.value));

  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg p-6 shadow-sm ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-2 bg-gray-200 rounded w-1/2"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (error && !subscription) {
    return (
      <div className={`bg-white rounded-lg p-6 shadow-sm border border-red-100 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Subscription Status</h3>
          <button 
            onClick={handleRefresh}
            className="text-gray-500 hover:text-gray-700"
          >
            <RefreshCw size={16} />
          </button>
        </div>
        <div className="p-4 bg-red-50 rounded-md text-red-700 flex items-start gap-3">
          <AlertTriangle className="mt-0.5" size={18} />
          <div>
            <p className="font-medium">Error Loading Subscription</p>
            <p className="text-sm text-red-600 mt-1">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-3 px-3 py-1 bg-white text-sm text-red-600 rounded border border-red-200 hover:bg-red-50"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Basic view (for header/compact display)
  if (!showDetailedView) {
    return (
      <div className={`${className}`}>
        <UsageIndicator compact />
      </div>
    );
  }

  // Full dashboard view
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-100 ${className}`}>
      {/* Header with Plan Info */}
      <div className="border-b border-gray-100 p-5">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-1">
              <span className={getPlanColor()}>
                {planIcon(planType as SubscriptionPlan)}
              </span>
              <span className={getPlanColor()}>
                {planType.charAt(0).toUpperCase() + planType.slice(1)} Plan
              </span>
              {hasActiveSubscription && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                  Active
                </span>
              )}
              {!hasActiveSubscription && (
                <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                  Inactive
                </span>
              )}
            </h3>
            <div className="flex items-center text-sm text-gray-500 mt-1">
              <Calendar size={14} className="mr-1" />
              <span>Renews on {formattedRenewalDate}</span>
              <span className="ml-2 px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                {daysUntilRenewal} days
              </span>
            </div>
          </div>
          <button 
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`p-2 rounded-full hover:bg-gray-100 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Dashboard Navigation */}
      <div className="border-b border-gray-100">
        <div className="flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === 'overview' 
                ? 'text-indigo-600 border-b-2 border-indigo-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === 'history' 
                ? 'text-indigo-600 border-b-2 border-indigo-600' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Usage History
          </button>
        </div>
      </div>

      {/* Usage Details */}
      <div className="p-5">
        {activeTab === 'overview' ? (
          <>
            <UsageIndicator showDetailed={false} className="mb-4" />
            
            {/* Status & Actions */}
            <div className="mt-6">
              {!hasActiveSubscription && (
                <div className="p-4 bg-amber-50 rounded-md text-amber-700 flex items-start gap-3 mb-4">
                  <AlertTriangle className="mt-0.5" size={18} />
                  <div>
                    <p className="font-medium">Subscription Not Active</p>
                    <p className="text-sm mt-1">Your subscription is currently inactive. Upgrade to continue using premium features.</p>
                  </div>
                </div>
              )}

              {hasActiveSubscription && usedCount >= limit && (
                <div className="p-4 bg-amber-50 rounded-md text-amber-700 flex items-start gap-3 mb-4">
                  <AlertTriangle className="mt-0.5" size={18} />
                  <div>
                    <p className="font-medium">Usage Limit Reached</p>
                    <p className="text-sm mt-1">You've used all your analyses for this billing period. Consider upgrading your plan for more analyses.</p>
                  </div>
                </div>
              )}

              {hasActiveSubscription && usedCount < limit && (
                <div className="p-4 bg-green-50 rounded-md text-green-700 flex items-start gap-3 mb-4">
                  <CheckCircle className="mt-0.5" size={18} />
                  <div>
                    <p className="font-medium">Subscription Active</p>
                    <p className="text-sm mt-1">You have {remaining} analyses remaining this billing period.</p>
                  </div>
                </div>
              )}

              {/* Plan Features */}
              <div className="mt-6 bg-gray-50 rounded-md p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Plan Includes</h4>
                <ul className="space-y-2">
                  <li className="flex text-sm">
                    <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-600">
                      <span className="font-medium">{limit} analyses</span> per month
                    </span>
                  </li>
                  {isPremium && (
                    <>
                      <li className="flex text-sm">
                        <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">
                          Advanced candidate matching
                        </span>
                      </li>
                      <li className="flex text-sm">
                        <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">
                          Priority support
                        </span>
                      </li>
                    </>
                  )}
                  {isStandard && (
                    <li className="flex text-sm">
                      <CheckCircle size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">
                        Basic candidate matching
                      </span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6">
                <Link
                  to="/pricing"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm font-medium flex items-center"
                >
                  {isFree ? 'Upgrade Plan' : 'Manage Subscription'}
                  <ChevronRight size={16} className="ml-1" />
                </Link>
                
                {usedCount >= limit * 0.8 && !isPremium && (
                  <Link
                    to="/pricing"
                    className="px-4 py-2 bg-white border border-indigo-200 text-indigo-600 rounded-md hover:bg-indigo-50 text-sm font-medium flex items-center"
                  >
                    Get More Analyses
                    <ArrowUpRight size={14} className="ml-1" />
                  </Link>
                )}
              </div>
            </div>
          </>
        ) : (
          // Usage History Tab
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-medium text-gray-700">Usage History (Current Month)</h4>
              <div className="text-xs text-gray-500">
                {usedCount} analyses used out of {limit}
              </div>
            </div>
            
            {/* Chart/Graph of usage */}
            <div className="bg-white rounded-md border border-gray-100 p-3 mb-5">
              <div className="flex h-40 items-end space-x-1">
                {usageHistory.map((day, i) => (
                  <div 
                    key={i} 
                    className="flex-1 flex flex-col items-center"
                    title={`${day.date}: ${day.value} analyses`}
                  >
                    <div 
                      className={`w-full ${
                        day.value > (limit / 30) * 1.5 
                          ? 'bg-amber-400' 
                          : day.value > (limit / 30) 
                              ? 'bg-blue-400' 
                              : 'bg-indigo-400'
                      } rounded-t`}
                      style={{ 
                        height: `${(day.value / Math.max(highestDailyUsage, 1)) * 100}%`,
                        minHeight: day.value > 0 ? '4px' : '0'
                      }}
                    ></div>
                    {i % 5 === 0 && (
                      <div className="text-xs text-gray-500 mt-1 whitespace-nowrap overflow-hidden text-ellipsis" style={{maxWidth: '40px'}}>
                        {day.date}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="h-px bg-gray-100 w-full my-3"></div>
              <div className="flex justify-between text-xs text-gray-500">
                <div>Daily Usage</div>
                <div>Average: {(usedCount / Math.max(1, today.getDate())).toFixed(1)}/day</div>
              </div>
            </div>
            
            {/* Usage Statistics */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-indigo-50 rounded-md p-3">
                <div className="text-xs text-indigo-600 mb-1">Daily Average</div>
                <div className="text-xl font-bold text-indigo-700">
                  {(usedCount / Math.max(1, today.getDate())).toFixed(1)}
                </div>
                <div className="text-xs text-indigo-500 mt-1">
                  analyses per day
                </div>
              </div>
              <div className="bg-blue-50 rounded-md p-3">
                <div className="text-xs text-blue-600 mb-1">Projected Usage</div>
                <div className="text-xl font-bold text-blue-700">
                  {Math.round(usedCount / (today.getDate() / 30) * 30)}
                </div>
                <div className="text-xs text-blue-500 mt-1">
                  analyses this month
                </div>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 mt-4">
              Note: This chart shows a visualization of your usage pattern. The actual daily breakdown may vary.
            </div>
          </div>
        )}
      </div>

      {/* Plan Comparison (if not premium) */}
      {!isPremium && (
        <div className="border-t border-gray-100 p-5 bg-gray-50 rounded-b-lg">
          <p className="text-sm font-medium text-gray-700 mb-3">
            Upgrade to Premium and get:
          </p>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-500" />
              <span>1,500 analyses per month (instead of {isStandard ? '500' : '10'})</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-500" />
              <span>Advanced candidate matching algorithms</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle size={14} className="text-green-500" />
              <span>Priority customer support</span>
            </li>
          </ul>
          
          <Link
            to="/pricing"
            className="mt-4 block w-full py-2 bg-indigo-600 text-white text-center rounded-md hover:bg-indigo-700 text-sm font-medium"
          >
            View Pricing Options
          </Link>
        </div>
      )}
    </div>
  );
};

export default UsageDashboard; 